
/**
 * Secure HTML utilities with universal (client/server) sanitization
 * Enhanced security implementation to prevent XSS attacks
 */

import DOMPurify from 'isomorphic-dompurify';

/**
 * Security configuration for different content types
 */
const SECURITY_CONFIGS = {
  // Configuration for blog content - allows rich formatting but removes scripts
  BLOG_CONTENT: {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'b', 'i', 'strong', 'em', 
      'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'hr', 'table', 'thead', 
      'tbody', 'tr', 'th', 'td', 'img', 'figure', 'figcaption', 'div', 'span'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'id',
      'width', 'height', 'data-*'
    ],
    FORBIDDEN_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur'],
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: true
  },
  
  // Configuration for excerpts and simple text - more restrictive
  EXCERPT: {
    ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'strong', 'em', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    FORBIDDEN_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur'],
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false
  },
  
  // Configuration for chat messages - allows markdown elements
  CHAT: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'h1', 'h2', 'h3', 'li', 'code', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    FORBIDDEN_ATTR: ['onclick', 'onload', 'onerror', 'onmouseover', 'onfocus', 'onblur'],
    KEEP_CONTENT: true,
    ALLOW_DATA_ATTR: false
  }
};

/**
 * Enhanced secure HTML sanitization with universal compatibility
 * @param html - HTML string to sanitize
 * @param contentType - Type of content for appropriate security level
 * @returns Sanitized HTML string
 */
export const secureHtmlSanitize = (
  html: string, 
  contentType: 'BLOG_CONTENT' | 'EXCERPT' | 'CHAT' = 'BLOG_CONTENT'
): string => {
  if (!html || typeof html !== 'string') {
    console.warn('[SecureHtmlUtils] Invalid HTML input provided');
    return '';
  }

  try {
    const config = SECURITY_CONFIGS[contentType];
    
    // Enhanced DOMPurify configuration
    const purifyConfig = {
      ...config,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false,
      SANITIZE_DOM: true,
      // Remove all script tags and event handlers
      FORBID_TAGS: ['script', 'object', 'embed', 'applet', 'meta', 'link'],
      FORBID_ATTR: [
        ...config.FORBIDDEN_ATTR,
        'style' // Remove inline styles to prevent CSS injection
      ],
      // Custom hook to remove dangerous attributes
      SANITIZE_NAMED_PROPS_PREFIX: false,
      // Additional security measures
      USE_PROFILES: { html: true }
    };

    // Sanitize the HTML
    const sanitized = DOMPurify.sanitize(html, purifyConfig);
    
    // Additional validation - ensure no script content leaked through
    if (sanitized.toLowerCase().includes('<script') || 
        sanitized.toLowerCase().includes('javascript:') ||
        sanitized.toLowerCase().includes('data:text/html')) {
      console.error('[SecureHtmlUtils] Potentially dangerous content detected and blocked');
      return 'Contenuto bloccato per motivi di sicurezza';
    }

    // Log sanitization activity in development
    if (process.env.NODE_ENV === 'development' && html !== sanitized) {
      console.log('[SecureHtmlUtils] Content was sanitized', {
        contentType,
        originalLength: html.length,
        sanitizedLength: sanitized.length
      });
    }

    return sanitized;
  } catch (error) {
    console.error('[SecureHtmlUtils] Error during sanitization:', error);
    return 'Errore nella sanitizzazione del contenuto';
  }
};

/**
 * Secure HTML entity decoding with XSS prevention
 * @param text - String with HTML entities to decode
 * @returns Decoded string
 */
export const secureDecodeHtmlEntities = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  try {
    // First pass - handle common entities safely
    let decoded = text
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'")
      .replace(/&#8217;/g, "'")  // Right single quotation mark
      .replace(/&#8216;/g, "'")  // Left single quotation mark
      .replace(/&#8220;/g, '"')  // Left double quotation mark
      .replace(/&#8221;/g, '"')  // Right double quotation mark
      .replace(/&rsquo;/g, "'")  // Right single quotation mark (named entity)
      .replace(/&lsquo;/g, "'")  // Left single quotation mark (named entity)
      .replace(/&rdquo;/g, '"')  // Right double quotation mark (named entity)
      .replace(/&ldquo;/g, '"')  // Left double quotation mark (named entity)
      .replace(/&hellip;/g, "...")
      .replace(/\[&hellip;\]/g, "...");

    // Server-side or client-side decoding
    if (typeof document !== 'undefined') {
      // Browser environment - use DOM for comprehensive decoding
      const textarea = document.createElement('textarea');
      textarea.innerHTML = decoded;
      decoded = textarea.value;
    }
    
    // Security check - ensure no script injection through entity decoding
    if (decoded.toLowerCase().includes('<script') || 
        decoded.toLowerCase().includes('javascript:')) {
      console.warn('[SecureHtmlUtils] Potential script injection blocked in entity decoding');
      return text; // Return original text if suspicious content detected
    }
    
    return decoded;
  } catch (error) {
    console.error('[SecureHtmlUtils] Error in entity decoding:', error);
    return text;
  }
};

/**
 * Strip HTML tags and decode entities securely
 * @param html - HTML string to clean
 * @returns Plain text without HTML tags
 */
export const secureStripHtmlAndDecodeEntities = (html: string): string => {
  if (!html || typeof html !== 'string') return '';
  
  try {
    // First sanitize to remove any dangerous content
    const sanitized = secureHtmlSanitize(html, 'EXCERPT');
    
    // Remove HTML tags
    const withoutTags = sanitized.replace(/<[^>]+>/g, '');
    
    // Decode entities securely
    return secureDecodeHtmlEntities(withoutTags);
  } catch (error) {
    console.error('[SecureHtmlUtils] Error in strip HTML and decode:', error);
    return '';
  }
};

/**
 * Validate that HTML content is safe for rendering
 * @param html - HTML to validate
 * @returns Boolean indicating if content is safe
 */
export const validateSecureHtml = (html: string): boolean => {
  if (!html || typeof html !== 'string') return false;
  
  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /data:text\/html/i,
    /vbscript:/i,
    /onload=/i,
    /onerror=/i,
    /onclick=/i,
    /onmouseover=/i,
    /<iframe[^>]*src=[^>]*javascript:/i,
    /<object/i,
    /<embed/i,
    /<applet/i
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(html));
};

/**
 * Legacy compatibility - enhanced version of original sanitizeHtml
 */
export const sanitizeHtml = (html: string): string => {
  return secureHtmlSanitize(html, 'BLOG_CONTENT');
};

/**
 * Legacy compatibility - enhanced version of original decodeHtmlEntities
 */
export const decodeHtmlEntities = (text: string): string => {
  return secureDecodeHtmlEntities(text);
};

/**
 * Legacy compatibility - enhanced version of original stripHtmlAndDecodeEntities
 */
export const stripHtmlAndDecodeEntities = (html: string): string => {
  return secureStripHtmlAndDecodeEntities(html);
};
